import { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageView, PageTopBar } from '../../components'
import { Loader } from '../../components/Loader'
import { fetchWarehouseOperators, fetchWorkShifts, fetchOperatorRates } from '../../services/workforceServices'
import { WorkforceTabs } from './WorkforceTabs'
import { AssignOperatorSlidePanel } from './components/AssignOperatorSlidePanel'
import { CONTRACT_TYPE_BADGE_CLASS, formatIsoDate } from '../../utils/workforceUtils'

const ContractBadge = ({ contract }) => {
  const cls = CONTRACT_TYPE_BADGE_CLASS[contract] || CONTRACT_TYPE_BADGE_CLASS.CUBBO
  return <span className={`inline-flex px-2 py-0.5 text-xs rounded-full border ${cls}`}>{contract}</span>
}

export const WarehouseOperatorsContainer = () => {
  const { warehouse_id } = useParams()
  const { t } = useTranslation()

  const [search, setSearch] = useState('')
  const [shiftFilter, setShiftFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [panel, setPanel] = useState(null) // null | { operator: null | object }

  const queryKey = ['workforce', warehouse_id, 'operators', { search, shiftFilter, typeFilter, showInactive }]
  const { data, isLoading, isError, error } = useQuery(
    queryKey,
    () =>
      fetchWarehouseOperators(warehouse_id, {
        ...(search ? { full_name_or_email: search } : {}),
        ...(shiftFilter ? { work_shift_id: shiftFilter } : {}),
        ...(typeFilter ? { operator_type: typeFilter } : {}),
        ...(showInactive ? { include_inactive: true } : {}),
      }),
    { keepPreviousData: true },
  )

  const operators = useMemo(() => {
    const raw = Array.isArray(data) ? data : data?.warehouse_operators || data?.operators || []
    return raw
  }, [data])

  const { data: shiftsData } = useQuery(
    ['workforce', warehouse_id, 'shifts', { include_inactive: true }],
    () => fetchWorkShifts(warehouse_id, { include_inactive: true }),
    { refetchOnWindowFocus: false },
  )
  const shifts = useMemo(() => (Array.isArray(shiftsData) ? shiftsData : shiftsData?.work_shifts || shiftsData?.shifts || []), [shiftsData])

  const { data: ratesData } = useQuery(['workforce', warehouse_id, 'rates', {}], () => fetchOperatorRates(warehouse_id, {}), {
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  })
  const operatorTypes = useMemo(() => {
    const raw = Array.isArray(ratesData) ? ratesData : ratesData?.operator_rates || ratesData?.rates || []
    return [...new Set(raw.map(r => r.operator_type).filter(Boolean))].sort()
  }, [ratesData])

  return (
    <>
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">{t('workforce.operators.title')}</div>
            <button
              type="button"
              onClick={() => setPanel({ operator: null })}
              className="inline-flex items-center rounded-lg bg-yellow-500 px-3 py-2 text-sm font-medium text-white hover:bg-yellow-600"
            >
              + {t('workforce.operators.assign')}
            </button>
          </PageTopBar>
        }
        topMenuFullWidth={true}
        childrenFullWidth={true}
      >
        <WorkforceTabs />

        <div className="flex flex-wrap gap-2 items-center mt-4">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('workforce.operators.search_placeholder')}
            className="border border-gray-300 rounded-md text-sm px-3 py-2 min-w-[220px]"
          />
          <select value={shiftFilter} onChange={e => setShiftFilter(e.target.value)} className="border border-gray-300 rounded-md text-sm px-3 py-2 bg-white">
            <option value="">{t('workforce.operators.all_shifts')}</option>
            {shifts.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="border border-gray-300 rounded-md text-sm px-3 py-2 bg-white">
            <option value="">{t('workforce.operators.all_types')}</option>
            {operatorTypes.map(ot => (
              <option key={ot} value={ot}>
                {ot}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            {t('workforce.operators.include_inactive')}
          </label>
        </div>

        {isLoading ? (
          <div className="mt-6">
            <Loader show={true} />
          </div>
        ) : isError ? (
          <div className="mt-4 rounded bg-red-50 text-red-700 p-3 text-sm">{error?.message || t('workforce.errors.generic')}</div>
        ) : operators.length === 0 ? (
          <div className="mt-6 p-8 text-center text-gray-500 border rounded bg-white">{t('workforce.operators.empty')}</div>
        ) : (
          <div className="bg-white mt-4 border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('workforce.operators.name')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('workforce.operators.operator_type')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('workforce.operators.contract_type')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('workforce.operators.work_shift')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('workforce.operators.columns.start_date')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('workforce.operators.columns.end_date')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('workforce.operators.status')}</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {operators.map(op => (
                  <tr key={op.id}>
                    <td className="px-4 py-2 text-sm">
                      <div className="font-medium">{op.user_name || op.user?.full_name || op.full_name || '—'}</div>
                      <div className="text-xs text-gray-500">{op.user_email || op.user?.email || op.email}</div>
                    </td>
                    <td className="px-4 py-2 text-sm">{op.operator_type}</td>
                    <td className="px-4 py-2 text-sm">
                      <ContractBadge contract={op.contract_type} />
                    </td>
                    <td className="px-4 py-2 text-sm">{op.work_shift?.name || shifts.find(s => s.id === op.work_shift_id)?.name || '—'}</td>
                    <td className="px-4 py-2 text-sm">{formatIsoDate(op.start_date)}</td>
                    <td className="px-4 py-2 text-sm">{formatIsoDate(op.end_date)}</td>
                    <td className="px-4 py-2 text-sm">
                      {op.operator_active ? (
                        <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-green-50 text-green-700 border border-green-200">
                          {t('workforce.operators.active')}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                          {t('workforce.operators.inactive')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setPanel({ operator: op })}
                        className="text-xs border border-gray-300 rounded-md px-2 py-1 text-gray-700 hover:bg-gray-50"
                      >
                        {t('workforce.edit')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageView>

      {panel && <AssignOperatorSlidePanel warehouse_id={warehouse_id} operator={panel.operator} onClose={() => setPanel(null)} />}
    </>
  )
}
